import ActivityFeed from "components/owner-app/activity-feed/ActivityFeed";
import UserActionLog from "components/owner-app/user-action-log/UserActionLog";
import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { BbotTabs, Breadcrumbs, Tabs, TOPContainer } from "top-component-library";

export const SYSTEM_LOGS_TAB_PARAM = "tab";

// Slugs deliberately match the standalone routes these feeds used to live at, so
// /integrations-activity-feed maps onto ?tab=integrations-activity-feed.
export const SYSTEM_LOGS_TABS = {
  AUDIT_TRAIL: "audit-trail",
  INTEGRATIONS_ACTIVITY: "integrations-activity-feed",
  PRINTER_ACTIVITY: "printer-activity-feed",
  FINANCIAL_ACTIVITY: "financial-activity-feed",
};

export const DEFAULT_SYSTEM_LOGS_TAB = SYSTEM_LOGS_TABS.AUDIT_TRAIL;

const VALID_TABS = Object.values(SYSTEM_LOGS_TABS);

// A feed deep link can be opened before the venue has resolved, and ActivityFeed
// fetches on mount without checking that it has a customer_id. Holding the feed
// back matches how the standalone activity feed pages guard it.
const FeedPane = ({ selectedCustomer, parameters }) =>
  selectedCustomer ? (
    <ActivityFeed endpoint={"/owner/getFeed"} endpointParameters={parameters} />
  ) : (
    <p>Please select a customer from the dropdown above.</p>
  );

const SystemLogsPage = (props) => {
  const { selectedCustomer, userInfo } = props;

  const history = useHistory();
  const location = useLocation();

  const customerId = selectedCustomer?.customer_id;

  // ActivityFeed refetches whenever this object's identity changes, so it is built
  // once per venue rather than inline, where every render of this page would
  // trigger another round of feed requests.
  const feedParameters = useMemo(
    () => ({
      integrations: { customer_id: customerId, feed_type: "pos" },
      printers: { customer_id: customerId, feed_type: "print_" },
      financial: { customer_id: customerId, feed_type: "finance" },
    }),
    [customerId]
  );

  // The URL is the only source of truth for the open tab, so a link or a refresh
  // lands on the same feed the sender was looking at. An unrecognised slug falls
  // back to the default instead of leaving AntD on a key that matches no pane,
  // which would render the tab strip above an empty body.
  const activeKey = useMemo(() => {
    const requested = new URLSearchParams(location.search).get(SYSTEM_LOGS_TAB_PARAM);
    return VALID_TABS.includes(requested) ? requested : DEFAULT_SYSTEM_LOGS_TAB;
  }, [location.search]);

  // replace, not push: clicking through all four feeds should not bury the page
  // the user arrived from under four history entries.
  const selectTab = useCallback(
    (key) => {
      const params = new URLSearchParams(location.search);
      params.set(SYSTEM_LOGS_TAB_PARAM, key);
      history.replace({ pathname: location.pathname, search: `?${params.toString()}` });
    },
    [history, location.pathname, location.search]
  );

  return (
    <TOPContainer>
      <Breadcrumbs name={"System Logs"} link={"/system-logs"} />

      <div className={"margin-bottom-2"}>
        <h2>System Logs</h2>
      </div>

      <BbotTabs activeKey={activeKey} onChange={selectTab}>
        <Tabs.TabPane tab="Audit Trail" key={SYSTEM_LOGS_TABS.AUDIT_TRAIL}>
          <h4>Audit Trail</h4>
          <p>View a log of actions performed by a user.</p>
          <UserActionLog customerId={selectedCustomer?.customer_id} userInfo={userInfo} />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Integrations Activity Feed" key={SYSTEM_LOGS_TABS.INTEGRATIONS_ACTIVITY}>
          <h4>Integrations Activity Feed</h4>
          <p>View possible issues with your integrations.</p>
          <FeedPane selectedCustomer={selectedCustomer} parameters={feedParameters.integrations} />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Printer Activity Feed" key={SYSTEM_LOGS_TABS.PRINTER_ACTIVITY}>
          <h4>Printer Activity Feed</h4>
          <p>View possible issues with your printer(s).</p>
          <FeedPane selectedCustomer={selectedCustomer} parameters={feedParameters.printers} />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Financial Activity Feed" key={SYSTEM_LOGS_TABS.FINANCIAL_ACTIVITY}>
          <h4>Financial Activity Feed</h4>
          <p>View financial activity and related issues.</p>
          <FeedPane selectedCustomer={selectedCustomer} parameters={feedParameters.financial} />
        </Tabs.TabPane>
      </BbotTabs>
    </TOPContainer>
  );
};

export default SystemLogsPage;
